import React, { useContext, useState } from 'react'

// @mui material components
import { Icon, IconButton, TextField } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { saveAs } from 'file-saver'

import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import {
	ALLOWED_ATTACHMENT_INPUT_TYPES,
	ALLOWED_FILE_TYPES,
	MAX_FILE_SIZE_BYTES,
	MAX_FILE_SIZE_MB,
} from 'layouts/feedback/constants'
import { CustomerFeedback } from 'layouts/feedback/customer/types'
import { RemoteFile } from 'layouts/feedback/types'
import { getFilenameFromFirebaseUrl } from 'layouts/feedback/utils'
import FormField from 'modules/FormField'

import { FormContext } from '../../constants'

function Details(): JSX.Element {
	const formContext = useContext(FormContext)
	const { localForm, setFormEdited, setLocalForm, showError } = formContext
	const [uploadError, setUploadError] = useState<string | null>(null)

	const errorVisible = showError

	const handleFormUpdate = (value: any, field: keyof CustomerFeedback['form']['details']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				details: {
					...prev.form.details,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			setUploadError(`${file.name}: File type not supported.`)
			return
		}
		if (file.size > MAX_FILE_SIZE_BYTES) {
			setUploadError(
				`${file.name} must be less than ${MAX_FILE_SIZE_MB}MB. File size is ${(
					file.size /
					(1024 * 1024)
				).toFixed(1)}MB.`
			)
			return
		}
		if (localForm.form.details.attachments.length >= 5) {
			setUploadError('Cannot upload more than 5 attachments.')
			return
		}
		handleFormUpdate(
			[...localForm.form.details.attachments, { type: 'local', file }],
			'attachments'
		)
		setUploadError(null)
		setFormEdited(true)
		event.target.value = '' // Reset file input to allow re-uploading the same file
	}

	const handleLocalDownload = (file: File) => {
		if (!file) return
		saveAs(file, file.name)
	}

	const deleteLocalAttachment = (file: File) => {
		if (!file) return

		const updatedAttachments = localForm.form.details.attachments.filter(
			attachment => attachment.type === 'local' && attachment.file !== file
		)
		handleFormUpdate(updatedAttachments, 'attachments')
		setFormEdited(true)
		setUploadError(null)
	}

	const markRemoteAttachmentForDeletion = (fileIndex: number) => {
		const updatedAttachments = [...localForm.form.details.attachments]
		updatedAttachments[fileIndex] = {
			...updatedAttachments[fileIndex],
			delete: true, // Mark the remote file for deletion
		} as RemoteFile

		handleFormUpdate(updatedAttachments, 'attachments')
		setFormEdited(true)
		setUploadError(null)
	}

	return (
		<Card id="details">
			<MDBox component="form" p={2}>
				<Grid container spacing={3} alignItems="center">
					<Grid item xs={12}>
						<MDBox
							display={'flex'}
							flexDirection={'row'}
							justifyContent={'space-between'}
							alignItems={'center'}
						>
							<MDBox display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
								<MDAvatar bgColor="primary" size="xs">
									1
								</MDAvatar>
								<MDTypography variant="h5" fontWeight="medium">
									Details
								</MDTypography>
							</MDBox>
							<MDBox display={'flex'} gap={2}></MDBox>
						</MDBox>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							label="Incident Date/Time"
							inputProps={{ type: 'datetime-local' }}
							value={localForm.form.details?.incidentDatetime ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'incidentDatetime')
							}}
							error={errorVisible && !localForm.form.details?.incidentDatetime}
							helperText={
								errorVisible && !localForm.form.details?.incidentDatetime
									? 'Date/Time is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							label="Job Number (optional)"
							value={localForm.form.details?.jobNumber ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'jobNumber')
							}}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<FormField
							label="Booking Phone Number (optional)"
							value={localForm.form.details?.bookingPhoneNumber ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'bookingPhoneNumber')
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							disabled={false}
							label={`Details Of Incident`}
							multiline
							rows={5} // Adjust number of rows for the size of the textarea
							variant="outlined"
							placeholder={
								'Please provide a detailed description of the incident.\nBe sure to mention anyone who was involved and include any important information'
							}
							InputLabelProps={{ shrink: true }}
							fullWidth
							value={localForm.form.details?.incidentDetails ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'incidentDetails')
							}}
							error={showError && !localForm.form.details?.incidentDetails}
							helperText={
								showError && !localForm.form.details?.incidentDetails
									? 'Details of Incident is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12}>
						<MDTypography variant="h6">Attachments:</MDTypography>
					</Grid>
					{localForm.form.details.attachments?.map((item, index) => (
						<Grid item xs={12} key={index}>
							<MDBox
								display={'flex'}
								alignItems={'center'}
								justifyContent={'space-between'}
								color="secondary"
							>
								<MDBox display={'flex'} alignItems={'center'} flexDirection={'row'}>
									<MDTypography
										onClick={() => {
											if (item.type === 'local') {
												handleLocalDownload(item.file)
											}
										}}
										variant="button"
										color="secondary"
										fontWeight="medium"
										sx={{
											cursor: 'pointer',
											textDecoration: 'none',
											'&:hover': {
												textDecoration: 'underline',
											},
										}}
									>
										<i>
											{item.type === 'local'
												? item.file.name
												: getFilenameFromFirebaseUrl(item.url)}
										</i>
									</MDTypography>
									{item.type === 'local' && (
										<Icon fontSize={'small'} color="secondary" sx={{ ml: 2, fontSize: 12 }}>
											cloud_upload
										</Icon>
									)}
									{item.type === 'remote' && item.delete && (
										<MDTypography variant={'caption'} color="error" sx={{ ml: 2 }}>
											<i>Marked for deletion</i>
										</MDTypography>
									)}
								</MDBox>
								<IconButton
									size="small"
									color="error"
									onClick={() => {
										if (item.type === 'local') {
											deleteLocalAttachment(item.file)
										} else {
											markRemoteAttachmentForDeletion(index)
										}
									}}
								>
									<Icon fontSize={'small'} color="error" sx={{ ml: 1 }}>
										delete_outline
									</Icon>
								</IconButton>
							</MDBox>
						</Grid>
					))}
					<Grid item xs={12} sm={8}>
						<MDButton component="label" color="info" variant="outlined">
							<MDInput
								accept={ALLOWED_ATTACHMENT_INPUT_TYPES}
								type="file"
								id="upload-attachment"
								style={{ display: 'none' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
							/>
							Upload Attachment&nbsp;
							<Icon>upload</Icon>
						</MDButton>
						{uploadError && (
							<MDTypography variant="caption" color="error" mt={2} display="block">
								{uploadError}
							</MDTypography>
						)}
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default Details
